import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, MailCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LogoTile from "@/components/app/LogoTile";
import AuthLayout from "@/components/app/AuthLayout";

const ForgotPasswordPage = () => {
    const { user } = useAuth();
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    if (user && user !== false) return <Navigate to="/reports" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await api.post("/auth/password-reset/request", { email });
            setSent(true);
        } catch (err) {
            const s = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (s === 429 && detail?.error === "TOO_MANY_ATTEMPTS") {
                setError(
                    `Too many requests. Try again in ${detail.retry_after_seconds}s.`
                );
            } else {
                setError(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthLayout testId="forgot-password-page">
            <div className="min-h-screen w-full flex items-center justify-center px-4 py-10 route-fade">
            <div className="w-full max-w-[440px]">
                <div className="flex flex-col items-center mb-8">
                    <LogoTile size={72} className="mb-5" />
                    <h1 className="text-3xl font-semibold text-slate-900 text-center">
                        Forgot password?
                    </h1>
                    <p className="text-[14px] text-slate-600 mt-2 text-center">
                        Enter the email you signed up with. We'll send you a reset link.
                    </p>
                </div>

                <div className="glass-card">
                    {sent ? (
                        <div className="flex flex-col items-start gap-4" data-testid="forgot-success">
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700">
                                <MailCheck size={20} strokeWidth={2} />
                            </div>
                            <p className="text-[14px] text-slate-700 leading-relaxed">
                                If that email is registered, a reset link is on its way. Check
                                spam if you don't see it in a few minutes. The link expires in 30
                                minutes.
                            </p>
                            <Link to="/login" className="btn btn-secondary btn-sm">
                                <ArrowLeft size={14} strokeWidth={2} />
                                Back to log in
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="flex flex-col gap-4" data-testid="forgot-form">
                            <div>
                                <label className="label label-required" htmlFor="fp-email">
                                    Email
                                </label>
                                <input
                                    id="fp-email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input"
                                    data-testid="forgot-email-input"
                                />
                            </div>
                            {error && (
                                <div
                                    className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2"
                                    data-testid="forgot-error"
                                >
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                className="btn btn-primary w-full py-3 mt-1"
                                disabled={busy}
                                data-testid="forgot-submit-button"
                            >
                                {busy ? "Sending…" : "Send reset link"}
                            </button>
                            <Link
                                to="/login"
                                className="text-[13px] text-emerald-700 hover:text-emerald-800 self-center mt-1"
                                data-testid="forgot-back-link"
                            >
                                ← Back to log in
                            </Link>
                        </form>
                    )}
                </div>
            </div>
            </div>
        </AuthLayout>
    );
};

export default ForgotPasswordPage;
